import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Typography } from '@mui/material';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';

import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import VisitScheduleForm, { type VisitScheduleFormHandle } from 'src/components/VisitScheduleForm';
import DeleteVisitModal from 'src/components/modals/DeleteVisitModal';
import { useModalManager } from 'src/components/modals/ModalManager';
import { cronToDayNameMap } from 'src/constants/Visit';
import { AnalyticsContext } from 'src/contexts/AnalyticsContext';
import { useSnackbar } from 'src/contexts/SnackbarContext';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import {
    useDeleteVisitScheduleMutation,
    useGetVisitScheduleQuery,
    useUpdateVisitScheduleMutation,
} from 'src/store/sageHomeApi';
import { getTeamMemberDisplayName } from 'src/utils/nameUtils';
import { getDisplayDaysFromCron, getDisplayTimeRangeFromEpoch } from 'src/utils/visitUtils';

export const Route = createFileRoute('/(app)/visits/edit/$visitScheduleRid')({
    component: EditVisitComponent,
});

const PageName = 'Edit Visit';

function EditVisitComponent() {
    const { trackPageView } = useContext(AnalyticsContext);
    const navigate = useNavigate();
    const { visitScheduleRid } = useParams({ from: '/(app)/visits/edit/$visitScheduleRid' });

    const { showSuccess, showError } = useSnackbar();
    const { openModal } = useModalManager();
    const { careRecipient, getTeamMemberDetails } = useTeamMemberData();

    const formRef = useRef<VisitScheduleFormHandle>(null);
    const [isFormValid, setIsFormValid] = useState(false);

    const {
        data: currentSchedule,
        isLoading: isLoadingSchedule,
        error,
    } = useGetVisitScheduleQuery(
        {
            careRecipientRid: careRecipient?.teamMemberRid || '',
            visitScheduleRid,
        },
        { skip: !visitScheduleRid || !careRecipient?.teamMemberRid }
    );

    const isRepeating = !!currentSchedule?.cronExpression;

    const [updateVisitSchedule, { isLoading: isSubmitting }] = useUpdateVisitScheduleMutation();

    const [deleteVisitSchedule, { isLoading: isDeleting }] = useDeleteVisitScheduleMutation();

    const handleDelete = async () => {
        await deleteVisitSchedule({
            careRecipientRid: careRecipient?.teamMemberRid || '',
            visitScheduleRid,
        });
        showSuccess('Visit schedule deleted');
        navigate({ to: isRepeating ? '/visits/repeating-visits' : '/visits' });
    };

    const caregiver = getTeamMemberDetails(currentSchedule?.caregiverRid || '');

    const handleConfirmDelete = () => {
        openModal({
            content: DeleteVisitModal,
            props: {
                repeatingDays: currentSchedule?.cronExpression
                    ? getDisplayDaysFromCron(currentSchedule.cronExpression)
                    : undefined,
                // TODO: Add date string if available from visit
                dateString: undefined,
                timeString:
                    currentSchedule?.scheduleStartInstant && currentSchedule?.visitDurationMinutes
                        ? getDisplayTimeRangeFromEpoch(
                              currentSchedule.scheduleStartInstant,
                              currentSchedule.visitDurationMinutes
                          )
                        : undefined,
                caregiverName: caregiver ? getTeamMemberDisplayName(caregiver) : undefined,
                onContinue: handleDelete,
            },
        });
    };

    const formDefaults = useMemo(() => {
        if (!currentSchedule) return undefined;

        // Parse cron expression to get days and time (if repeating)
        let selectedDays: string[] | undefined;
        let hour24: number;
        let minute: number;
        let selectedDate: Date;

        if (currentSchedule.cronExpression) {
            // Cron format: minute hour * * dayOfWeek
            // Day of week: 0=Sunday, 1=Monday, ..., 6=Saturday
            const cronParts = currentSchedule.cronExpression.split(' ');
            const dayPart = cronParts[4]; // day of week
            if (dayPart && dayPart !== '*') {
                selectedDays = dayPart
                    .split(',')
                    .map((d: string) => cronToDayNameMap[parseInt(d)])
                    .filter(Boolean)
                    .map((day) => day.toUpperCase()); // Convert to uppercase: 'Mon' -> 'MON'
            }

            // For repeating visits, derive time from cron expression
            hour24 = parseInt(cronParts[1], 10);
            minute = parseInt(cronParts[0], 10);
            selectedDate = currentSchedule.scheduleStartInstant
                ? new Date(currentSchedule.scheduleStartInstant)
                : new Date();
        } else {
            // For non-repeating visits, parse time from scheduleStartInstant
            const scheduleStartInstant = currentSchedule.scheduleStartInstant
                ? new Date(currentSchedule.scheduleStartInstant)
                : new Date();

            hour24 = scheduleStartInstant.getHours();
            minute = scheduleStartInstant.getMinutes();
            selectedDate = scheduleStartInstant;
        }

        // Convert hour24 to hour12 and determine AM/PM
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const amPm: 'AM' | 'PM' = hour24 >= 12 ? 'PM' : 'AM';

        return {
            caregiverRid: currentSchedule.caregiverRid || '',
            selectedDate,
            selectedHour: hour12,
            selectedMinute: minute,
            selectedAmPm: amPm,
            visitDurationMinutes: currentSchedule.visitDurationMinutes || 60,
            selectedDays,
            cronExpression: currentSchedule.cronExpression,
            ianaTimezone: currentSchedule.ianaTimezone || 'America/New_York',
        };
    }, [currentSchedule]);

    const onSubmit = async (data: {
        caregiverRid: string;
        scheduleStartInstant: number;
        visitDurationMinutes: number;
        cronExpression?: string;
        ianaTimezone?: string;
    }) => {
        if (!careRecipient?.teamMemberRid) {
            showError('Care recipient not found');
            return;
        }

        try {
            const updateRequest = {
                caregiverRid: data.caregiverRid,
                scheduleStartInstant: data.scheduleStartInstant,
                visitDurationMinutes: data.visitDurationMinutes,
                cronExpression: data.cronExpression,
                ianaTimezone: data.ianaTimezone, // Form always provides this value
            };

            await updateVisitSchedule({
                careRecipientRid: careRecipient.teamMemberRid,
                visitScheduleRid,
                updateVisitScheduleRequest: updateRequest,
            }).unwrap();

            showSuccess('Visit schedule updated');
            navigate({ to: isRepeating ? '/visits/repeating-visits' : '/visits' });
        } catch (error) {
            console.error('Failed to update visit schedule:', error);
            showError('An error occurred, please try again.');
        }
    };

    const handleSubmit = () => {
        formRef.current?.submit();
    };

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InnerMaxWidthContainer>
            <PageHeader pageTitle={isRepeating ? 'Edit Repeat Visit' : 'Edit One-Time Visit'} />

            {isLoadingSchedule ? (
                <Typography variant="body1">Loading...</Typography>
            ) : error ? (
                <Typography color="error" variant="body1">
                    There was an error loading the visit.
                </Typography>
            ) : (
                <VisitScheduleForm
                    ref={formRef}
                    isRepeating={isRepeating}
                    showChangeCaregiverButton
                    defaultValues={formDefaults}
                    onSubmit={onSubmit}
                    onValidityChange={setIsFormValid}
                />
            )}

            <PageFooter includeShadow>
                <Button
                    variant="contained"
                    fullWidth
                    color="errorInverse"
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}
                >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
                <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!isFormValid || isSubmitting}
                >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
            </PageFooter>
        </InnerMaxWidthContainer>
    );
}
